import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { default as api } from '../../utils/api';
import { CheckCircle, XCircle } from 'lucide-react';

const VerifyPrescription = () => {
    const { prescriptionId } = useParams();
    const [verificationData, setVerificationData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const verify = async () => {
            try {
                // Use a direct axios call if needed, or api instance if it handles public routes gracefully
                const response = await api.get(`/prescription/verify/${prescriptionId}`);
                if (response.data.success) {
                    setVerificationData(response.data.data);
                } else {
                    setError('Invalid Prescription');
                }
            } catch (err) {
                console.error("Verification error:", err);
                setError(err.response?.data?.message || 'Verification Failed');
            } finally {
                setLoading(false);
            }
        };

        verify();
    }, [prescriptionId]);

    if (loading) return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
            <div className="max-w-md w-full bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="bg-dark p-6 text-center">
                    <h1 className="text-xl font-bold text-gold">Dr. Vivara Healthcare</h1>
                    <p className="text-gray-400 text-sm">Official Verification Portal</p>
                </div>

                <div className="p-8">
                    {error ? (
                        <div className="text-center">
                            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h2>
                            <p className="text-gray-600">{error}</p>
                        </div>
                    ) : (
                        <div className="text-center space-y-6">
                            <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />

                            <div>
                                <h2 className="text-2xl font-bold text-gray-900">Valid Prescription</h2>
                                <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full mt-2">
                                    Official Record Found
                                </span>
                            </div>

                            <div className="border-t border-b border-gray-100 py-4 text-left space-y-3">
                                <div>
                                    <label className="text-xs text-gray-500 uppercase">Prescribed By</label>
                                    <p className="font-semibold text-gray-800">Dr. {verificationData.doctorName}</p>
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 uppercase">Patient Name</label>
                                    <p className="font-semibold text-gray-800">{verificationData.patientName}</p>
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 uppercase">Date of Issue</label>
                                    <p className="font-semibold text-gray-800">
                                        {new Date(verificationData.issuedDate).toLocaleDateString('en-IN', {
                                            year: 'numeric', month: 'long', day: 'numeric'
                                        })}
                                    </p>
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 uppercase">Signed At</label>
                                    <p className="font-semibold text-gray-800">
                                        {new Date(verificationData.signedAt).toLocaleString('en-IN')}
                                    </p>
                                </div>
                            </div>

                            <p className="text-xs text-gray-400">
                                ID: {verificationData._id}
                            </p>
                        </div>
                    )}
                </div>

                <div className="bg-gray-50 px-6 py-4 text-center">
                    <p className="text-xs text-gray-500">&copy; 2025 Dr. Vivara Healthcare. All rights reserved.</p>
                </div>
            </div>
        </div>
    );
};

export default VerifyPrescription;
